// De lade van één supportgesprek, met de markup van het prototype (supDrawer in design/build/prototype.js): gegevens,
// het verloop uit de bewaarde tijdstippen (ontvangen, opgenomen, naar Archief) en Openen in Outlook.
// Alleen lezen: opnemen, antwoorden en archiveren gebeurt in Outlook. De medewerker ziet bij een wachtend gesprek hoe hij
// het zelf opneemt (een categorie in Outlook); de planner koppelt een onbekend afzenderdomein aan een klant.
// Bij een wachtend gesprek ziet de planner "Wie kan dit opnemen": de developers met de meeste vrije uren deze week
// (capaciteit uit Clockify), bij gelijke ruimte wie de minste open gesprekken heeft. Wie boven de capaciteit zit of
// vandaag afwezig is, staat er niet bij. Zonder Clockify valt dit deel weg.
import { useMemo } from "react";
import { fmtHours, freeHours } from "../lib/api-clockify";
import { useSupport, type SupportConversation } from "../lib/api-support";
import { absentOn, LOAD_OPTS, TONE_CLS, useCapacityView } from "../lib/api-teams";
import { useIndex, usePlanning } from "../lib/hooks";
import { Icon } from "../lib/icons";
import { addDays } from "../lib/planning";
import { devLabel, hoursSince, klantText, nOf, waitText, when } from "../lib/support";
import { useAppState } from "../shell/AppState";
import { Av } from "./Avatar";

const names = (xs: string[]) => (xs.length < 2 ? xs.join("") : xs.slice(0, -1).join(", ") + " en " + xs[xs.length - 1]);
const safeLink = (u: string | null) => (u && /^https:\/\//i.test(u) ? u : null);

/** Wie dit gesprek kan opnemen: de drie developers met de meeste vrije uren deze week; null zonder capaciteit. */
function useWhoCanPick(on: boolean) {
  const cv = useCapacityView(on);
  const { klant } = useAppState();
  // dezelfde gegevens als het scherm Support (uit de cache): wie support opneemt en hoeveel gesprekken die open heeft
  const { data: sup } = useSupport(klant);
  const { data } = usePlanning();
  const idx = useIndex(data);
  return useMemo(() => {
    if (!on || !cv) return null;
    const today = cv.cap.today, sunday = addDays(cv.cap.weeks[0].monday, 6);
    const devs = (sup?.devs || []).filter((d) => d.personId);
    const open = new Map(devs.map((d) => [d.personId!, d.open]));
    // de developers uit de supportmailbox; zonder (nog) gegevens: iedereen in Clockify
    const pool = devs.length ? devs.map((d) => d.personId!) : [...cv.people.keys()];
    return pool.flatMap((pid) => {
      const cp = cv.people.get(pid), p = idx.people.get(pid);
      if (!cp || !p || absentOn(cv, cp, today)) return [];
      const w = cv.load(cp, 0), tone = cv.tone(w.pct);
      if (w.pct == null || tone === "overvol") return [];
      return [{ p, pct: w.pct, tone, free: freeHours(cp, cv.cap.weeks, today, sunday, LOAD_OPTS), open: open.get(pid) || 0 }];
    }).sort((a, b) => b.free - a.free || a.open - b.open || a.p.name.localeCompare(b.p.name)).slice(0, 3);
  }, [on, cv, sup, idx]);
}

export function SupportConvDrawer({ conv, mailbox, med, myCategory, onClose, onLink }: {
  conv: SupportConversation;
  mailbox: string;
  med: boolean;
  /** de categorie die de medewerker in Outlook gebruikt */
  myCategory: string;
  onClose: () => void;
  /** planner: dit afzenderdomein aan een klant koppelen */
  onLink?: (domain: string) => void;
}) {
  const has = conv.devs.length > 0;
  const who = names(conv.devs.map(devLabel));
  const sugg = useWhoCanPick(!med && !has);
  const link = safeLink(conv.webLink);
  const klant = conv.klant ? conv.klant + ", via het afzenderdomein"
    : conv.domainState === "onbekend" ? conv.domain + ", nog niet gekoppeld"
    : conv.domainState === "genegeerd" ? "geen klant: " + conv.domain + " telt niet mee per klant"
    : "geen klant: alleen mail van TLCC";
  const kv: [string, string][] = [
    ["Afzender", conv.from || "onbekend"], ["Klant", klant], ["Berichten", String(conv.messages)],
    ["Ontvangen", when(conv.received)], [has ? "Open sinds" : "Wacht al", waitText(hoursSince(conv.openSince))],
  ];
  const steps = [
    { label: "Ontvangen in " + mailbox, when: when(conv.received), cls: "p-step done" },
    {
      label: has ? "Opgenomen door " + who + ", categorie " + names(conv.devs.map((d) => d.category)) : "Nog niet opgenomen",
      when: has ? (conv.picked ? when(conv.picked) : "tijdstip niet bekend") : "", cls: "p-step" + (has ? " done" : " now"),
    },
    { label: "Afgewerkt: naar de map Archief", when: "", cls: "p-step" + (has ? " now" : "") },
  ];

  return (
    <>
      <button type="button" className="p-scrim" aria-label="Sluiten" onClick={onClose} />
      <aside className="p-drawer" role="dialog" aria-modal="true" aria-labelledby="st">
        <div className="p-drawer-h">
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
            <div style={{ minWidth: 0 }}><p className="ex-eyebrow" style={{ margin: 0 }}>Support · {klantText(conv)}</p><h2 id="st">{conv.subject}</h2></div>
            <button type="button" className="ex-icon-btn ex-icon-btn--square" aria-label="Sluiten" title="Sluiten" onClick={onClose}><Icon name="x" size={18} /></button>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
            <span className={"ex-pill ex-pill--" + (has ? "ok" : "warn")}><span className="ex-dot" />{has ? "In behandeling bij " + who : "Wacht op een developer"}</span>
          </div>
        </div>
        <div className="p-drawer-b" style={{ overflowY: "auto" }}>
          <div className="p-fs"><h3>Gesprek</h3>
            {kv.map(([k, v]) => <div key={k} className="p-kv"><span>{k}</span><b>{v}</b></div>)}
            {conv.domainState === "onbekend" && (
              <p className="p-kd-note" style={{ margin: "10px 0 0" }}>Dit afzenderdomein hoort nog bij geen klant. {med || !onLink
                ? "De planner koppelt het aan een klant."
                : <button type="button" className="ex-link" onClick={() => onLink(conv.domain)}>Domein koppelen</button>}</p>
            )}
          </div>
          <div className="p-fs"><h3>Verloop</h3>
            <div className="p-steps">{steps.map((x) => <div key={x.label} className={x.cls}><i /><span><b>{x.label}</b><small>{x.when}</small></span></div>)}</div>
          </div>
          {sugg && (
            <div className="p-fs"><h3>Wie kan dit opnemen</h3>
              <p className="p-kd-note" style={{ margin: "0 0 10px" }}>Collega's met de meeste vrije uren deze week, bij gelijke ruimte de minste open gesprekken eerst. Wie boven de capaciteit zit of vandaag afwezig is, staat er niet bij. Opnemen doet u in Outlook met de categorie van de developer.</p>
              {sugg.length ? (
                <div className="p-capc">
                  {sugg.map((x) => (
                    <div key={x.p.id} className="p-cand">
                      <Av id={x.p.id} init={x.p.initials} />
                      <span><b>{x.p.name}</b><span className="p-capc-n t-mute">{nOf(x.open, "open gesprek", "open gesprekken")} · {x.pct} % bezet deze week</span></span>
                      <span className={TONE_CLS[x.tone]} style={{ fontSize: 14, fontWeight: 700, whiteSpace: "nowrap" }}>{fmtHours(x.free, 0)} vrij</span>
                    </div>
                  ))}
                </div>
              ) : <p className="p-empty" style={{ padding: 0 }}>Niemand heeft deze week nog ruimte.</p>}
            </div>
          )}
          {med && !has && (
            <div className="p-fs"><h3>Zelf opnemen</h3>
              <p className="p-kd-note" style={{ margin: 0 }}>Neemt u het op? Geef het gesprek in Outlook de categorie {myCategory}. Daarna staat het hier onder In behandeling.</p>
              {link && <div className="p-quick" style={{ marginTop: 10 }}><a className="ex-btn-xs ex-btn-xs--primary" href={link} target="_blank" rel="noreferrer"><Icon name="ext" size={13} />Openen in Outlook</a></div>}
            </div>
          )}
          <div className="ex-notice ex-notice--mist p-notice"><Icon name="info" size={16} /><p>Deze versie leest de mailbox alleen. Opnemen, antwoorden en archiveren gebeurt in Outlook.</p></div>
        </div>
        <div className="p-drawer-f">
          <span className="t-mute" style={{ fontSize: 13 }}>{mailbox} · alleen metadata</span>
          <span style={{ display: "flex", gap: 8 }}>
            <button type="button" className="ex-btn" onClick={onClose}>Sluiten</button>
            {link && <a className="ex-btn ex-btn--primary" href={link} target="_blank" rel="noreferrer"><Icon name="ext" size={15} />Openen in Outlook</a>}
          </span>
        </div>
      </aside>
    </>
  );
}
